import type { InjectionKey } from 'vue'
import storefrontAr from '~/locales/storefront-ar.json'

/**
 * True inside the storefront page tree. Lets `t()` / `isRtl` follow the store
 * language there without dragging the dashboard along during navigation.
 */
export const STOREFRONT_I18N = Symbol('storefront-i18n') as InjectionKey<boolean>

/**
 * True while a client navigation is crossing the dashboard ↔ storefront
 * boundary. The outgoing page is still on screen, so dir/lang must not move.
 */
export function useLocaleNavHold() {
  return useState('locale-nav-hold', () => false)
}

// Touch this module when nav labels change so Vite invalidates the cached
// dictionary (avoids raw keys in the UI).
// rev: langs-es-pt-hi-ru-tr-it + storefront-subset-2026-08-15d + store-coach-tabs + wizard-store-lang + admin-show-2026-08-18 + simple-product-mission + create-store-errors + domain-live-check + collection-open-drawer + coach-press-create + add-products-collection-label + product-card-tab + editor-components-tab + create-online-store + collection-style-mission + create-progress-pairs + app-home-hydrate-full-dict + coach-publish-preview-lesson + auth-phone-country + pdp-create-collection-fab + pdp-variants-toggle + pdp-gallery-image-editor + pdp-gallery-add-photos + pdp-gallery-object-scale + pdp-gallery-on-image-adjust + pdp-gallery-image-bar + pdp-gallery-remove-bg + pdp-gallery-photos-label + card-templates-label + designs-subtabs-templates + store-page-templates-term + collection-page-templates-btn + cards-templates-label + collection-components-btn + home-create-product-page-fabs + wizard-two-products-pages + wizard-domain-taken-switch + wizard-demo-products-step + pdp-name-coach-cloud + add-product-menu-ask + create-product-assign-pages + first-launch-create-product + first-product-auto-create + home-welcome-then-create + pdp-coach-follow-trainer + store-trainer-storefront-subset + creator-action-storefront-subset + pdp-layout-device-preview + trainer-nav-announce-2026-08-28 + trainer-nav-logo-2026-08-28 + trainer-add-page-2026-08-28 + trainer-add-component-2026-08-28 + owner-bar-collapse-2026-08-29 + store-ai-placeholder-ssr + create-product-store-lang-demo + collection-layout4-filter-bar + product-card-layout10-where-to-buy + product-card-layout10-add-to-cart-price + collection-layout5-filter-bar + product-card-layout11 + collection-layout6-editorial + storefront-utf8-2026-09-11 + card-price-save-amount-2026-09-14 + split-layout-cta-labels-2026-09-16 + split-remove-overlay-2026-09-16 + wizard-contact-email-2026-09-20 + wizard-store-plan-2026-09-20 + open-store-manager-2026-09-25 + owner-contact-email-2026-09-25 + hero-edit-hint-2026-09-25 + home-landing-trainer-2026-09-25 + photo-ui-edit-ask-2026-10-03 + photo-ui-edit-prompt-popup + photo-ui-edit-choice-2026-10-03 + erp-setup-ai-2026-10-07 + receivable-income-ask + receivable-income-date + erp-currencies-2026-10-13 + erp-clinic-preset + erp-manage-2026-10-13 + erp-page-access + shops-amazon-nav + shop-location-label + themes-coach-2026-10-20 + themes-step-progress-2026-10-20 + themes-coach-guide-2026-10-20 + hero-setup-steps-2026-10-21 + hero-setup-progress-2026-10-21

export const KAWADERNA_LANGS = [
  'ar',
  'en',
  'es',
  'pt',
  'hi',
  'ru',
  'tr',
  'it',
] as const

export type KawadernaLang = (typeof KAWADERNA_LANGS)[number]

/** Each language in its own script so a first-run picker is readable. */
export const KAWADERNA_LANG_NATIVE: Record<KawadernaLang, string> = {
  ar: 'العربية',
  en: 'English',
  es: 'Español',
  pt: 'Português',
  hi: 'हिन्दी',
  ru: 'Русский',
  tr: 'Türkçe',
  it: 'Italiano',
}

/** Set after the visitor confirms a language on the auth landing. */
export const LANG_CHOSEN_STORAGE_KEY = 'hr-lang-chosen'

export function hasChosenLanguage(): boolean {
  if (!import.meta.client) return false
  return localStorage.getItem(LANG_CHOSEN_STORAGE_KEY) === '1'
}

export function markLanguageChosen() {
  if (!import.meta.client) return
  localStorage.setItem(LANG_CHOSEN_STORAGE_KEY, '1')
}

/** Set after a first-time guest picks shopping or building a store. */
export const GUEST_INTENT_STORAGE_KEY = 'hr-guest-intent'

export type GuestIntent = 'shops' | 'jobs' | 'store'

export function hasChosenGuestIntent(): boolean {
  if (!import.meta.client) return false
  const value = localStorage.getItem(GUEST_INTENT_STORAGE_KEY)
  return value === 'shops' || value === 'jobs' || value === 'store'
}

export function markGuestIntent(intent: GuestIntent) {
  if (!import.meta.client) return
  localStorage.setItem(GUEST_INTENT_STORAGE_KEY, intent)
}

export type LocalizedText =
  | Partial<Record<KawadernaLang, string>>
  | string
  | null
  | undefined

const LANG_SET = new Set<string>(KAWADERNA_LANGS)

export function isKawadernaLang(value: unknown): value is KawadernaLang {
  return typeof value === 'string' && LANG_SET.has(value)
}

export function normalizeKawadernaLang(value: unknown, fallback: KawadernaLang = 'ar'): KawadernaLang {
  return isKawadernaLang(value) ? value : fallback
}

/** BCP 47 locales used for number formatting. */
const NUMBER_LOCALES: Record<KawadernaLang, string> = {
  ar: 'ar-EG',
  en: 'en',
  es: 'es',
  pt: 'pt',
  hi: 'hi-IN',
  ru: 'ru',
  tr: 'tr',
  it: 'it',
}

/** Same locale on the server and in the browser — `undefined` used Node vs the shopper. */
export function storefrontNumberLocale(lang: string | null | undefined): string {
  return NUMBER_LOCALES[normalizeKawadernaLang(lang, 'en')] || 'en'
}

export function formatStorefrontMoney(
  amount: number,
  currency: string,
  lang?: string | null,
  fractionDigits?: number,
): string {
  const code = String(currency || 'EGP').toUpperCase()
  const digits = fractionDigits == null
    ? undefined
    : Math.min(20, Math.max(0, Math.round(fractionDigits)))
  try {
    return new Intl.NumberFormat(storefrontNumberLocale(lang), {
      style: 'currency',
      currency: code,
      ...(digits == null
        ? {}
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    }).format(amount)
  }
  catch {
    const whole = digits === 0 ? String(Math.round(amount)) : String(amount)
    return `${whole} ${code}`
  }
}

const MONEY_BIDI_MARKS = /[\u200e\u200f\u061c]/g
/** ASCII, Eastern Arabic, and Persian digits plus grouping/decimal marks. */
const MONEY_NUMBER_RUN = /[\d\u0660-\u0669\u06F0-\u06F9][\d\u0660-\u0669\u06F0-\u06F9\s.,'\u00a0\u066b\u066c\u060c\u2011-]*/

/**
 * Split a formatted money string so the numeric amount can be edited while the
 * currency stays visible. `\d` alone misses Eastern Arabic digits, and stripping
 * punctuation from codes like `ج.م.` used to invert the two parts.
 */
export function splitFormattedMoney(
  formatted: string,
  amount: number,
): { prefix: string; amount: string; suffix: string } {
  const numeric = String(amount)
  const cleaned = String(formatted || '').replace(MONEY_BIDI_MARKS, '')
  const match = cleaned.match(MONEY_NUMBER_RUN)
  if (!match || match.index == null) {
    return { prefix: '', amount: cleaned.trim() || numeric, suffix: '' }
  }
  return {
    prefix: cleaned.slice(0, match.index).trim(),
    amount: match[0].trim() || numeric,
    suffix: cleaned.slice(match.index + match[0].length).trim(),
  }
}

/**
 * Swap the formatted currency token for a display label.
 * Empty label keeps the storefront symbol. The store currency code is untouched.
 */
export function displayCurrencyParts(
  parts: { prefix: string, suffix: string },
  label: string,
): { prefix: string, suffix: string } {
  const custom = String(label || '').trim()
  if (!custom) return { prefix: parts.prefix, suffix: parts.suffix }
  if (parts.prefix) return { prefix: custom, suffix: '' }
  return { prefix: '', suffix: custom || parts.suffix }
}

function emptyDictionaries(): Record<KawadernaLang, Record<string, string>> {
  return Object.fromEntries(
    KAWADERNA_LANGS.map(lang => [lang, {}]),
  ) as Record<KawadernaLang, Record<string, string>>
}

/** Vite JSON may be the dict or `{ default: dict }` depending on CJS/ESM interop. */
function unwrapLocaleJson(mod: unknown): Record<string, string> {
  if (!mod || typeof mod !== 'object') return {}
  const rec = mod as { default?: unknown }
  if (rec.default && typeof rec.default === 'object' && !Array.isArray(rec.default)) {
    return rec.default as Record<string, string>
  }
  return mod as Record<string, string>
}

function emptySlimMaps(): Record<KawadernaLang, Record<string, string>> {
  return {
    ...emptyDictionaries(),
    ar: unwrapLocaleJson(storefrontAr),
  }
}

/**
 * Kept beside the slim maps rather than replacing them: this module is shared
 * by every SSR request, so swapping in the full dictionary meant one `/app`
 * render left it in place for later storefront HTML. Those pages were then
 * rendered with keys the storefront bundle does not ship, and hydration
 * replaced the text with raw ids (`nav.stores`).
 */
const fullMessages = shallowReactive<Record<KawadernaLang, Record<string, string>>>(
  emptyDictionaries(),
)

const FULL_LOADERS: Record<KawadernaLang, () => Promise<{ default: Record<string, string> }>> = {
  ar: () => import('../locales/ar.json'),
  en: () => import('../locales/en.json'),
  es: () => import('../locales/es.json'),
  pt: () => import('../locales/pt.json'),
  hi: () => import('../locales/hi.json'),
  ru: () => import('../locales/ru.json'),
  tr: () => import('../locales/tr.json'),
  it: () => import('../locales/it.json'),
}

/**
 * Storefront subsets for every UI language (see `scripts/sync-storefront-locales.mjs`).
 * Arabic is inlined above; the rest load here so SSR `t()` does not fall back
 * to Arabic and mismatch the client.
 */
const SLIM_LOADERS: Partial<Record<KawadernaLang, () => Promise<{ default: Record<string, string> }>>> = {
  en: () => import('../locales/storefront-en.json'),
  es: () => import('../locales/storefront-es.json'),
  pt: () => import('../locales/storefront-pt.json'),
  hi: () => import('../locales/storefront-hi.json'),
  ru: () => import('../locales/storefront-ru.json'),
  tr: () => import('../locales/storefront-tr.json'),
  it: () => import('../locales/storefront-it.json'),
}

type FullMessageFlags = Partial<Record<KawadernaLang, boolean>>
type MessageMaps = Record<KawadernaLang, Record<string, string>>

type I18nRuntime = {
  rev: string
  slimMessages: MessageMaps
  loadingSlim: Partial<Record<KawadernaLang, Promise<void>>>
  fullMessages: MessageMaps
  fullLoaded: Partial<Record<KawadernaLang, boolean>>
  loadingFull: Partial<Record<KawadernaLang, Promise<void>>>
}

const I18N_RUNTIME_REV = 'create-website-2026-09-27-manager-wizard+logo-coach-start-2026-10-04+erp-hiring-2026-10-13+erp-van-history-2026-10-13+erp-qty-unit-fix-2026-10-13+erp-signup-options-2026-10-14+dashboard-build-sheet-2026-10-14+studio-how-walk-2026-10-14+mall-tab-2026-10-14+i18n-lookup-guard+shops-tab+shops-directory+auth-signin-default-2026-10-20+vntru-shops-admin-2026-10-21+inventory-manage-columns-2026-10-21+hero-setup-slim-2026-10-21+vntru-interest-2026-10-25+first-open-shop-or-build-2026-10-25+my-products-2026-10-25+inventory-categories-2026-10-25+categories-term-2026-10-25+builder-step-order-2026-10-26+inventory-edit-price-2026-10-27+wizard-reach-2026-10-28+nav-business-2026-10-28+builder-coach-short-2026-10-28+option-price-table-2026-10-28+step-categories-label-2026-10-28+themes-card-step-2026-10-28+step-pages-label-2026-10-28+step-website-label-2026-10-28+step-website-ar-2026-10-28+categories-coach-2026-10-28+inventory-coach-2026-10-28+categories-coach-copy-2026-10-28+inventory-coach-copy-2026-10-28+home-coach-copy-2026-10-28+themes-step-coach-2026-10-28+home-coach-copy-2026-10-29+setup-coach-7-steps-2026-10-29+nav-mall-shops-2026-10-29+nav-business-ar-2026-10-29+unpublished-link-i18n-2026-10-29+step-categories-short-2026-10-29+sku-ar-code-2026-10-29+sku-auto-2026-10-30+sku-seq-2026-10-30+picker-coach-2026-10-30+category-page-link-2026-10-31+create-category-wizard-2026-10-31+price-english-digits-2026-10-31+shared-gallery-all-variants-2026-10-31+product-review-confirm-2026-10-31+price-before-discount-ar-2026-10-31+inventory-copy-link-2026-10-31+category-drawer-step-2026-10-31'

type RequestI18nBag = {
  useFull: FullMessageFlags
  full: MessageMaps
}

type NuxtWithI18n = ReturnType<typeof tryUseNuxtApp> & {
  _kawadernaI18n?: RequestI18nBag
}

/**
 * Vite SSR can evaluate this file twice (`~/composables/...` vs auto-import).
 * Module-level maps then fill in one copy while `t()` reads the empty other,
 * which is how `/app` HTML shipped raw keys (`nav.home`) and hydrated Arabic.
 * `globalThis` is shared across those copies; the Nuxt app bag is per request.
 */
function i18nRuntime(): I18nRuntime {
  const g = globalThis as typeof globalThis & { __kawadernaI18nRuntime?: Partial<I18nRuntime> }
  const existing = g.__kawadernaI18nRuntime
  if (!existing?.fullMessages || existing.rev !== I18N_RUNTIME_REV) {
    const created: I18nRuntime = {
      rev: I18N_RUNTIME_REV,
      slimMessages: emptySlimMaps(),
      loadingSlim: {},
      fullMessages,
      fullLoaded: {},
      loadingFull: {},
    }
    g.__kawadernaI18nRuntime = created
    return created
  }
  existing.slimMessages ??= emptySlimMaps()
  existing.loadingSlim ??= {}
  existing.fullLoaded ??= {}
  existing.loadingFull ??= {}
  return existing as I18nRuntime
}

function requestI18nBag(): RequestI18nBag | null {
  const nuxtApp = tryUseNuxtApp() as NuxtWithI18n | null
  if (!nuxtApp) return null
  nuxtApp._kawadernaI18n ??= {
    useFull: {},
    full: emptyDictionaries(),
  }
  return nuxtApp._kawadernaI18n
}

function useFullMessageFlags() {
  return useState<FullMessageFlags>('kawaderna-full-messages', () => ({}))
}

/** Call before any `await` in the i18n plugin so the payload slot exists. */
export function primeKawadernaMessageState() {
  return useFullMessageFlags()
}

const HR_LANG_COOKIE_OPTS = {
  default: () => 'ar' as KawadernaLang,
  sameSite: 'lax' as const,
  maxAge: 60 * 60 * 24 * 365,
  watch: false as const,
}

/** Same cookie `useKawadernaI18n` reads — first caller must use these options. */
export function useHrLangCookie() {
  return useSharedCookie<KawadernaLang>('hr-lang', HR_LANG_COOKIE_OPTS)
}

/**
 * Register `kawaderna-lang` (and storefront override slots) before any `await`
 * so SSR HTML and the client payload share one app language.
 */
export function primeKawadernaLangState() {
  const langCookie = useHrLangCookie()
  const lang = useState<KawadernaLang>('kawaderna-lang', () =>
    normalizeKawadernaLang(langCookie.value, 'ar'),
  )
  if (import.meta.server) {
    lang.value = normalizeKawadernaLang(langCookie.value, 'ar')
  }
  useState<KawadernaLang | null>('storefront-lang-override', () => null)
  useState('storefront-lang-locked', () => false)
  return lang
}

function bindFullMessageFlags(): ReturnType<typeof useFullMessageFlags> | null {
  try {
    return useFullMessageFlags()
  }
  catch {
    const nuxtApp = tryUseNuxtApp()
    if (!nuxtApp) return null
    const flags = nuxtApp.runWithContext(() => useFullMessageFlags())
    return flags instanceof Promise ? null : flags
  }
}

function markFlag(flags: ReturnType<typeof useFullMessageFlags> | null, lang: KawadernaLang) {
  if (!flags || flags.value[lang]) return
  flags.value = { ...flags.value, [lang]: true }
}

function commitFullLang(
  lang: KawadernaLang,
  dict: Record<string, string>,
  nuxtApp: NuxtWithI18n | null,
  flags: ReturnType<typeof useFullMessageFlags> | null,
) {
  const runtime = i18nRuntime()
  runtime.fullMessages[lang] = dict
  runtime.fullLoaded[lang] = true
  if (nuxtApp) {
    nuxtApp._kawadernaI18n ??= {
      useFull: {},
      full: emptyDictionaries(),
    }
    nuxtApp._kawadernaI18n.useFull[lang] = true
    nuxtApp._kawadernaI18n.full[lang] = dict
  }
  markFlag(flags, lang)
}

function fullFlags(): FullMessageFlags {
  const fromState = bindFullMessageFlags()?.value ?? {}
  const fromBag = requestI18nBag()?.useFull ?? {}
  return { ...fromState, ...fromBag }
}

/** Pull the complete dictionary for `lang` — job platform and editor chrome. */
export function ensureFullKawadernaMessages(lang: KawadernaLang = 'ar'): Promise<void> {
  const target = normalizeKawadernaLang(lang)
  const runtime = i18nRuntime()
  // Capture before the JSON import. `tryUseNuxtApp()` is null in the `.then()`.
  const nuxtApp = tryUseNuxtApp() as NuxtWithI18n | null
  const flags = bindFullMessageFlags()
  if (
    !import.meta.dev
    && runtime.fullLoaded[target]
    && Object.keys(runtime.fullMessages[target] ?? {}).length
  ) {
    commitFullLang(target, runtime.fullMessages[target], nuxtApp, flags)
    return Promise.resolve()
  }
  const loadFull = () => FULL_LOADERS[target]().then((mod) => {
    const dict = unwrapLocaleJson(mod)
    runtime.fullMessages[target] = dict
    runtime.fullLoaded[target] = Object.keys(dict).length > 0
  })
  // Dev locale HMR must re-import; `??=` would keep the first stale dict.
  runtime.loadingFull[target] = import.meta.dev
    ? loadFull()
    : (runtime.loadingFull[target] ?? loadFull())
  return runtime.loadingFull[target]!.then(() => {
    commitFullLang(target, runtime.fullMessages[target] ?? {}, nuxtApp, flags)
  })
}

export function ensureFullEnglishMessages(): Promise<void> {
  return ensureFullKawadernaMessages('en')
}

/** Pull the storefront dictionary for `lang` (shopper-facing chrome). */
export function ensureKawadernaMessages(lang: KawadernaLang): Promise<void> {
  const target = normalizeKawadernaLang(lang)
  const runtime = i18nRuntime()
  // Keep the inlined Arabic map in sync with the JSON module. Vite SSR caches
  // `globalThis` across requests, so new keys would otherwise stay missing
  // until a server restart (SSR raw ids vs client translations).
  runtime.slimMessages.ar = unwrapLocaleJson(storefrontAr)
  if (target === 'ar') return Promise.resolve()

  const loader = SLIM_LOADERS[target]
  if (!loader) {
    return ensureFullKawadernaMessages(target).then(() => {
      if (!Object.keys(runtime.slimMessages[target]).length) {
        runtime.slimMessages[target] = runtime.fullMessages[target] ?? {}
      }
    })
  }
  // Production dictionaries are fixed at build; skip the extra import.
  // Dev must re-read so locale HMR actually reaches the next SSR render.
  if (!import.meta.dev && Object.keys(runtime.slimMessages[target]).length) {
    return Promise.resolve()
  }

  const pending = loader().then((mod) => {
    runtime.slimMessages[target] = unwrapLocaleJson(mod)
  })
  runtime.loadingSlim[target] = pending
  return pending
}

export function applyLanguageToDocument(value: KawadernaLang) {
  if (!import.meta.client) return
  document.documentElement.lang = value
  document.documentElement.dir = value === 'ar' ? 'rtl' : 'ltr'
  for (const code of KAWADERNA_LANGS) {
    document.body.classList.toggle(`lang-${code}`, value === code)
  }
}

export function resolveLocalizedText(
  value: LocalizedText,
  lang: KawadernaLang,
): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value[lang] ?? value.en ?? value.ar ?? ''
}

function lookupIn(
  lang: KawadernaLang,
  key: string,
  flags: FullMessageFlags,
  allowFull = true,
): string | undefined {
  if (allowFull) {
    const bag = requestI18nBag()
    if (flags[lang] || bag?.useFull[lang]) {
      const hit = bag?.full[lang]?.[key]
        ?? i18nRuntime().fullMessages[lang]?.[key]
        ?? fullMessages[lang]?.[key]
      if (hit !== undefined) return hit
    }
  }
  return i18nRuntime().slimMessages[lang]?.[key]
}

/**
 * Resolve a key in an explicit language. Storefront pages render in the store
 * language, so owner-facing editor chrome uses this to stay on the app one.
 */
export function translateIn(
  lang: KawadernaLang,
  key: string,
  params?: Record<string, string | number>,
): string {
  // Payload flags say whether this request's SSR used the full dictionary.
  // Storefront payloads leave them empty, so a leftover full map (dashboard
  // visit / HMR) cannot rewrite slim HTML. Job-platform payloads set them —
  // blocking those during hydration turned "Follow" into "home.follow" on
  // /app/home, and "Message" into the Arabic slim fallback.
  const flags = fullFlags()
  const hydrating = import.meta.client && Boolean(tryUseNuxtApp()?.isHydrating)
  const allowFull = !hydrating || Object.values(flags).some(Boolean)
  let text = lookupIn(lang, key, flags, allowFull)
    ?? lookupIn('en', key, flags, allowFull)
    ?? lookupIn('ar', key, flags, allowFull)
    ?? key
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      const formatted = typeof v === 'number'
        ? new Intl.NumberFormat(NUMBER_LOCALES[lang]).format(v)
        : String(v)
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), formatted)
    }
  }
  return text
}

export function useKawadernaI18n() {
  // Cookie is readable on both server and client, so SSR renders the user's
  // chosen language and hydration matches (no text/dir mismatch).
  const langCookie = useHrLangCookie()
  const lang = useState<KawadernaLang>('kawaderna-lang', () =>
    normalizeKawadernaLang(langCookie.value, 'ar'),
  )

  // Shared with useStorefrontLocale — when set, storefront UI (navbar, drawer,
  // product chrome) follows the store language instead of the app cookie.
  const storefrontOverride = useState<KawadernaLang | null>(
    'storefront-lang-override',
    () => null,
  )

  // Only the storefront page tree follows the override. The dashboard is still
  // mounted while `/store/{id}` loads, and must keep the app language.
  const inStorefront = getCurrentInstance() ? inject(STOREFRONT_I18N, false) : false

  const effectiveLang = computed<KawadernaLang>(() => {
    const override = storefrontOverride.value
    if (inStorefront && isKawadernaLang(override)) return override
    return lang.value
  })

  const isRtl = computed(() => effectiveLang.value === 'ar')

  function formatNumber(value: number | string | null | undefined): string {
    if (value == null || value === '') return ''
    const num = typeof value === 'number'
      ? value
      : Number(String(value).trim().replace(',', '.'))
    if (!Number.isFinite(num)) return String(value)
    return new Intl.NumberFormat(NUMBER_LOCALES[effectiveLang.value]).format(num)
  }

  function t(key: string, params?: Record<string, string | number>): string {
    return translateIn(effectiveLang.value, key, params)
  }

  function localized(value: LocalizedText): string {
    return resolveLocalizedText(value, effectiveLang.value)
  }

  function formatExperienceYears(
    value: number | null | undefined,
    style: 'full' | 'short' = 'full',
  ): string {
    if (value == null || !Number.isFinite(value)) return ''
    if (value <= 0) return t('experience.none')
    const label = style === 'short' ? t('applicants.years') : t('searchEmployee.years')
    return `${formatNumber(value)} ${label}`
  }

  function formatAge(dob: string | null | undefined): string {
    if (!dob?.trim()) return ''
    const birth = new Date(dob.trim())
    if (Number.isNaN(birth.getTime())) return ''

    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    if (age < 0 || age > 120) return ''

    return t('profile.yearsOld', { n: age })
  }

  async function setLanguage(next: KawadernaLang) {
    const normalized = normalizeKawadernaLang(next)
    // Flip only once the dictionary is in memory — otherwise `t()` resolves
    // through the Arabic fallback and the whole UI flashes Arabic first. The
    // app shell needs the complete dictionary, not the storefront subset.
    await ensureFullKawadernaMessages(normalized)
    lang.value = normalized
    langCookie.value = normalized
    if (import.meta.client) {
      // The shared ref never disposes and has `watch: false`, so persist here.
      document.cookie = `hr-lang=${encodeURIComponent(normalized)}; Path=/; Max-Age=${HR_LANG_COOKIE_OPTS.maxAge}; SameSite=Lax`
      localStorage.setItem('hr-lang', normalized)
      // Do not overwrite storefront dir/lang while browsing /store/*.
      if (storefrontOverride.value == null) {
        applyLanguageToDocument(normalized)
      }
    }
  }

  // Apply after mount only — an immediate watch mutates <html>/<body> during
  // hydration and fights useHead(htmlAttrs), which shows up as mismatches.
  // Register once, and only when a component instance is active: callers often
  // invoke this composable after `await` in async setup(), where onMounted()
  // would warn and leave hydration in a bad state.
  if (import.meta.client) {
    const docSyncReady = useState<boolean>('kawaderna-lang-doc-sync', () => false)
    if (!docSyncReady.value) {
      const startDocSync = () => {
        // Document dir/lang for the app cookie only. Storefront overrides are
        // applied after the store page is on screen (see storefront-locale).
        if (storefrontOverride.value == null) {
          applyLanguageToDocument(lang.value)
        }
        watch(lang, (value) => {
          if (storefrontOverride.value != null) return
          if (useLocaleNavHold().value) return
          applyLanguageToDocument(value)
        })
      }
      const instance = getCurrentInstance()
      if (instance) {
        docSyncReady.value = true
        onMounted(startDocSync)
      }
      else {
        const nuxtApp = tryUseNuxtApp()
        if (nuxtApp) {
          docSyncReady.value = true
          nuxtApp.hook('app:mounted', startDocSync)
        }
      }
    }
  }

  return {
    lang,
    effectiveLang,
    isRtl,
    t,
    localized,
    formatNumber,
    formatExperienceYears,
    formatAge,
    setLanguage,
    langs: KAWADERNA_LANGS,
  }
}
